/**
 * @fileoverview Local-first preview data service for `SystemDashboardBuilderPage` (`system-web`).
 * Queries `getSystemDb()` collections (`tenants`, `organizations`, `system_users`, `system_roles`,
 * `tiers`, `feature_flags`, `modules`, `import_templates`, `import_runs`, `report_definitions`,
 * `audit_logs`) to populate dashboard widgets during system-admin authoring.
 * @module system-web/services/systemDashboardDataService
 *
 * Responsibilities: - Provide `SAMPLE_PREVIEW_DEPARTMENT_ID` constant for the Preview Department selector.
 *                   - Query `getSystemDb()` collections directly for enabled dashboard widgets.
 *                   - Scope tenant-owned system collections (`tenants`, `import_runs`, `audit_logs`)
 *                     to `previewDepartmentId` when selected, while retaining global collections
 *                     (`tiers`, `modules`, `feature_flags`, etc.) intact.
 *                   - Resolve measure fields and aggregations (`count`, `countDistinct`, `sum`,
 *                     `avg`, `min`, `max`), returning `[]` when a non-count measure cannot be resolved.
 * Dependencies:     - @firesquirrel/local-db (getSystemDb, DashboardConfig, DashboardWidget)
 * Architecture:     - Local-first: reads exclusively from `getSystemDb()`. Zero API calls and
 *                     zero embedded mock or fictional property records.
 * Notes:            - Closes gap G-51 (`system-web`).
 *
 * @localFirst
 * Source of Truth: local (`getSystemDb()`)
 * Write Flow: read-only
 * Sync Strategy: N/A (read-only)
 * Conflict Resolution: N/A (read-only)
 */

import { getSystemDb, evaluateFilter, buildGlobalDashboardQueryFilters } from '@firesquirrel/local-db';
import type { DashboardConfig, DashboardWidget } from '@firesquirrel/local-db';
import { telemetry } from './telemetry';

export const SAMPLE_PREVIEW_DEPARTMENT_ID = '__sample_preview_department__';

const SYSTEM_COLLECTION_CUES = new Set([
  'tenants',
  'organizations',
  'system_users',
  'system_roles',
  'tiers',
  'feature_flags',
  'modules',
  'import_templates',
  'import_runs',
  'report_definitions',
  'audit_logs',
]);

/**
 * Only collections that carry real per-tenant ownership fields are filtered when a
 * specific department is selected in Preview Department. Global reference collections
 * (`tiers`, `modules`, `feature_flags`, `organizations`, `system_users`, `system_roles`,
 * `import_templates`, `report_definitions`) remain intact.
 */
const TENANT_SCOPED_SYSTEM_COLLECTIONS = new Set([
  'tenants',
  'import_runs',
  'audit_logs',
]);

interface ParsedSystemMeasure {
  aggregation: 'count' | 'countDistinct' | 'sum' | 'avg' | 'min' | 'max';
  field: string;
}

function parseSystemMeasure(measureKey: string): ParsedSystemMeasure {
  const raw = measureKey.trim();
  const fnMatch = /^(count|countDistinct|sum|avg|min|max)\(([^)]+)\)$/i.exec(raw);
  if (fnMatch) {
    const agg = fnMatch[1].toLowerCase();
    return {
      aggregation: (agg === 'countdistinct' ? 'countDistinct' : agg) as ParsedSystemMeasure['aggregation'],
      field: fnMatch[2].trim().split('.').pop() || '_id',
    };
  }

  const tail = raw.includes('.') ? raw.split('.').pop()! : raw;
  if (!tail || tail === 'count' || tail === 'value') {
    return { aggregation: 'count', field: '_id' };
  }
  if (tail.endsWith('Count') && tail !== 'recordCount') {
    return { aggregation: 'count', field: '_id' };
  }
  return { aggregation: 'sum', field: tail };
}

function computeMeasureValue(
  docs: Record<string, any>[],
  parsed: ParsedSystemMeasure,
): number | null {
  if (parsed.aggregation === 'count') {
    return docs.length;
  }
  if (parsed.aggregation === 'countDistinct') {
    const vals = docs
      .map((d) => d[parsed.field])
      .filter((v) => v !== undefined && v !== null);
    return new Set(vals.map(String)).size;
  }

  const nums = docs
    .map((d) => d[parsed.field])
    .filter((v) => v !== undefined && v !== null && v !== '' && !Number.isNaN(Number(v)))
    .map(Number);

  if (nums.length === 0) {
    return null;
  }

  switch (parsed.aggregation) {
    case 'sum':
      return nums.reduce((a, b) => a + b, 0);
    case 'avg':
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    case 'min':
      return Math.min(...nums);
    case 'max':
      return Math.max(...nums);
    default:
      return null;
  }
}

function matchesWidgetDataSourceFilters(
  doc: Record<string, any>,
  widget: DashboardWidget,
): boolean {
  const filters = widget.dataSource?.filters ?? [];
  for (const f of filters) {
    const fieldName = f.member.includes('.') ? f.member.split('.').pop()! : f.member;
    const val = doc[fieldName];
    const op = f.operator;
    const targets = f.values ?? [];
    if (op === 'equals' && !targets.some((t) => String(val) === String(t))) return false;
    if (op === 'notEquals' && !targets.every((t) => String(val) !== String(t))) return false;
    if (
      op === 'contains' &&
      !(typeof val === 'string' && targets.some((t) => val.toLowerCase().includes(String(t).toLowerCase())))
    ) {
      return false;
    }
  }
  return true;
}

/**
 * System-side member→field resolver injected into the shared
 * `buildGlobalDashboardQueryFilters` — system collections are addressed by
 * their leaf field name (e.g. `tenants.status` → `status`).
 */
function resolveSystemFilterField(member: string): { field: string } {
  return { field: member.includes('.') ? member.split('.').pop()! : member };
}

/**
 * @process querySystemWidgetRows
 * Steps:   1. Resolve the target system collection from `dataSource.cubeId`
 *             (default `tenants`).
 *          2. Read the Dexie table, drop `_deleted` docs, apply
 *             `dataSource.filters` then active interactive `globalFilters`.
 *          3. Scope tenant-owned collections by `previewDepartmentId` (global
 *             reference collections stay intact).
 *          4. Resolve the measure aggregation (count/countDistinct/sum/avg/
 *             min/max) and emit KPI rows or per-dimension chart rows.
 * Inputs:  db, widget, previewDepartmentId, config, globalFilters.
 * Outputs: `Record<string, unknown>[]` — `[]` when the measure is unresolvable
 *          or the collection is unavailable (never a fabricated count).
 * Failure Handling: measure that resolves to null → empty row set (explicit
 *   unsupported/empty state); missing table → `[]`.
 * Idempotency: REQUIRED — pure read over the supplied Dexie snapshot.
 */
async function querySystemWidgetRows(
  db: Record<string, any>,
  widget: DashboardWidget,
  previewDepartmentId: string,
  config: DashboardConfig,
  globalFilters: Record<string, unknown>,
): Promise<Record<string, unknown>[]> {
  const cubeId = widget.dataSource?.cubeId;
  const targetCollection =
    cubeId && SYSTEM_COLLECTION_CUES.has(cubeId) ? cubeId : 'tenants';

  const table = db[targetCollection];
  if (!table || typeof table.toArray !== 'function') {
    return [];
  }

  const globalQueryFilters = buildGlobalDashboardQueryFilters(
    config,
    widget,
    globalFilters,
    resolveSystemFilterField,
  );
  const allDocs: Record<string, any>[] = await table.toArray();
  let activeDocs = allDocs.filter(
    (d) =>
      d &&
      !d._deleted &&
      matchesWidgetDataSourceFilters(d, widget) &&
      globalQueryFilters.every((f) => evaluateFilter(d, f)),
  );

  if (
    previewDepartmentId &&
    previewDepartmentId !== SAMPLE_PREVIEW_DEPARTMENT_ID &&
    TENANT_SCOPED_SYSTEM_COLLECTIONS.has(targetCollection)
  ) {
    activeDocs = activeDocs.filter(
      (d) =>
        String(d._id ?? '') === previewDepartmentId ||
        String(d.tenantId ?? '') === previewDepartmentId ||
        String(d.slug ?? '') === previewDepartmentId,
    );
  }

  const measureKey =
    widget.kpiConfig?.measure ?? widget.dataSource?.measures?.[0] ?? 'count';
  const parsedMeasure = parseSystemMeasure(measureKey);
  const dimKey =
    widget.chartConfig?.xAxis ??
    widget.dataSource?.dimensions?.[0] ??
    'status';
  const rawDimField = dimKey.includes('.') ? dimKey.split('.').pop()! : dimKey;

  if (widget.type === 'kpi' || widget.type === 'gauge' || widget.type === 'progressBar') {
    const val = computeMeasureValue(activeDocs, parsedMeasure);
    if (val === null) return [];
    return [
      {
        name: widget.title || 'Total',
        category: widget.title || 'Total',
        value: val,
        [measureKey]: val,
      },
    ];
  }

  if (widget.type === 'chart') {
    if (activeDocs.length === 0) return [];
    const groups = new Map<string, Record<string, any>[]>();
    for (const doc of activeDocs) {
      const key = String(doc[rawDimField] ?? doc.status ?? doc.tier ?? doc.name ?? 'Unspecified');
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(doc);
    }
    const rows: Record<string, unknown>[] = [];
    for (const [category, groupDocs] of groups.entries()) {
      const val = computeMeasureValue(groupDocs, parsedMeasure);
      if (val === null) continue;
      rows.push({
        name: category,
        category,
        value: val,
        [dimKey]: category,
        [measureKey]: val,
      });
    }
    return rows;
  }

  return activeDocs.slice(0, 25).map((doc) => ({
    ...doc,
    name: String(doc.name ?? doc.displayName ?? doc._id ?? ''),
    category: String(doc[rawDimField] ?? doc.status ?? ''),
    value: Number(doc[parsedMeasure.field]) || 1,
  }));
}

/**
 * @process resolveSystemDashboardPreviewData
 * Steps:   1. Open `getSystemDb()` (empty map fallback when Dexie is
 *             unavailable, e.g. SSR/test).
 *          2. For each enabled widget, resolve preview rows from real system
 *             collections, honoring the Preview Department scope and active
 *             interactive `globalFilters`.
 *          3. Return a `{ [widgetId]: rows }` map consumed by the builder's
 *             Live Preview pane.
 * Inputs:  config, previewDepartmentId, globalFilters (interactive filter
 *          widget values keyed by filter id).
 * Outputs: `Record<string, Record<string, unknown>[]>` — one entry per enabled
 *          widget; `[]` means the widget rendered an empty/unsupported state.
 * Failure Handling: per-widget query errors are caught, telemetry'd
 *   (`system_dashboard_widget_query_failed`), and mapped to `[]` so one bad
 *   widget never breaks the preview.
 * Idempotency: REQUIRED — read-only; writes nothing to Dexie.
 * @localFirst
 * Source of Truth: local (`getSystemDb()`)
 * Write Flow: none (read-only)
 * Sync Strategy: N/A
 * Conflict Resolution: N/A
 */
export async function resolveSystemDashboardPreviewData(
  config: DashboardConfig,
  previewDepartmentId: string = SAMPLE_PREVIEW_DEPARTMENT_ID,
  globalFilters: Record<string, unknown> = {},
): Promise<Record<string, Record<string, unknown>[]>> {
  const resultMap: Record<string, Record<string, unknown>[]> = {};
  let db: Record<string, any> = {};
  try {
    db = (await getSystemDb()) as unknown as Record<string, any>;
  } catch (err) {
    console.warn('[systemDashboardDataService] getSystemDb unavailable:', err);
  }

  for (const widget of config.widgets ?? []) {
    if (widget.enabled === false) continue;

    try {
      resultMap[widget.id] = await querySystemWidgetRows(
        db,
        widget,
        previewDepartmentId,
        config,
        globalFilters,
      );
    } catch (err) {
      console.warn(
        `[systemDashboardDataService] Failed to query system DB for widget ${widget.id}:`,
        err,
      );
      telemetry.trackEvent('system_dashboard_widget_query_failed', {
        widgetId: widget.id,
        widgetType: widget.type,
      });
      resultMap[widget.id] = [];
    }
  }

  return resultMap;
}
